// components/pdf-shared/skip-link.tsx
//
// W8-SHELL (UX-10, VERDICT #45, 2026-10-06): a keyboard user on /pdf-editor Tabbed through the
// 7 site-nav stops (logo, Tools, Resources, Downloads, Search, Pricing, avatar) before the first
// editor control, and the page had no `main` landmark to jump to. Two pieces:
//   - `SkipLink`, rendered by `_app` as the FIRST focusable on the page, before the nav;
//   - `SkipTarget`, the `main` it lands on, wrapping the tool card in the single-page base.
// Scoped to /pdf-editor (`hasSkipLink`): the single-page base serves ~200 converters and changing
// their landmarks is a site-wide decision, not this lane's. Every other path renders exactly what
// it rendered before (no link; `SkipTarget` is a bare fragment).

import React from 'react';
import styles from './styles/skip-link.module.scss';

/** The `id` of the `main` the skip link targets. */
export const SKIP_TARGET_ID = 'main-content';

/** The page paths (`pageProps.path`, no leading slash) that carry a skip link + `main`. */
const SKIP_LINK_PATHS: ReadonlySet<string> = new Set(['pdf-editor']);

/** Whether the page at `path` gets the skip link and the `main` landmark. */
export const hasSkipLink = (path: string | null | undefined): boolean =>
    typeof path === 'string' && SKIP_LINK_PATHS.has(path);

/** The skip link: off-screen until focused, then a pill at the top-left. */
export const SkipLink: React.FC = () => (
    <a href={`#${SKIP_TARGET_ID}`} className={styles.skip} data-testid="skip-to-content">
        Skip to editor
    </a>
);

/**
 * The `main` landmark the skip link lands on (`tabIndex -1` so following the link moves focus
 * there and the next Tab is the first control inside). Inactive → the children unwrapped.
 */
export const SkipTarget: React.FC<{ active: boolean; children?: React.ReactNode }> = ({ active, children }) =>
    active
        ? <main id={SKIP_TARGET_ID} tabIndex={-1} className={styles.target}>{children}</main>
        : <>{children}</>;
